import { useState } from 'react';
import { motion } from 'motion/react';
import { snappyTween } from './motionUi';

/**
 * Submission Contract — shown on first session with a dominant character.
 * Cannot be closed without accepting.
 */
export default function SubmissionContractModal({
  characterName = '',
  nickname = '',
  tierTitles = [],
  kinksCatalog = [],
  selectedKinkIds = [],
  onKinksChange,
  realItemsCatalog = [],
  selectedRealItemIds = [],
  onRealItemsChange,
  onAccept
}) {
  const [kinksOpen, setKinksOpen] = useState(false);
  const [itemsOpen, setItemsOpen] = useState(false);

  const toggleKink = (id) => {
    if (!onKinksChange) return;
    const set = new Set(selectedKinkIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onKinksChange([...set]);
  };

  const toggleRealItem = (id) => {
    if (!onRealItemsChange) return;
    const set = new Set(selectedRealItemIds);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onRealItemsChange([...set]);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/85 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', damping: 22, stiffness: 280 }}
        className="vr-scroll-y max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-game-accent/30 bg-game-canvas shadow-2xl"
      >
        <div className="border-b border-white/10 bg-game-popup/40 px-6 py-4 text-center">
          <h2 className="text-lg font-bold tracking-tight text-game-accent">Submission Contract</h2>
        </div>

        <div className="space-y-5 px-6 py-5">
          <p className="text-center text-sm leading-relaxed text-game-body">
            I, <span className="font-bold text-game-text">{nickname || 'Player'}</span>, am here to please
            my Mistress <span className="font-bold text-game-accent">{characterName}</span>.
          </p>
          <p className="text-center text-xs leading-relaxed text-game-muted">
            I acknowledge my place and agree to be addressed by the titles bestowed upon me.
            I will strive to serve faithfully and fulfill my duties with devotion.
          </p>

          {tierTitles.length > 0 ? (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-game-muted">Approved Titles</p>
              <div className="flex flex-wrap gap-2">
                {tierTitles.map((t, i) => (
                  <span
                    key={i}
                    className="rounded-lg border border-game-accent/30 bg-game-accent/15 px-3 py-1.5 text-xs font-semibold text-game-accent"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2">
            <motion.button
              type="button"
              transition={snappyTween}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setKinksOpen((v) => !v)}
              className="rounded-xl border border-white/15 bg-game-surface px-4 py-2 text-xs font-semibold text-game-text"
            >
              Kinks ({selectedKinkIds.length}/{kinksCatalog.length})
            </motion.button>
            <motion.button
              type="button"
              transition={snappyTween}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setItemsOpen((v) => !v)}
              className="rounded-xl border border-white/15 bg-game-surface px-4 py-2 text-xs font-semibold text-game-text"
            >
              Items ({selectedRealItemIds.length}/{realItemsCatalog.length})
            </motion.button>
          </div>

          {kinksOpen ? (
            <div className="vr-scroll-y max-h-[min(40vh,280px)] space-y-1 overflow-y-auto rounded-xl border border-white/10 bg-game-surface/60 p-3">
              {kinksCatalog.map((k) => {
                const checked = selectedKinkIds.includes(String(k._id));
                return (
                  <label key={k._id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-game-body hover:bg-white/5">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleKink(String(k._id))}
                      className="accent-game-accent"
                    />
                    {k.name}
                  </label>
                );
              })}
              {kinksCatalog.length === 0 ? <p className="text-xs text-game-muted">No kinks configured.</p> : null}
            </div>
          ) : null}

          {itemsOpen ? (
            <div className="vr-scroll-y max-h-[min(40vh,280px)] space-y-3 overflow-y-auto rounded-xl border border-white/10 bg-game-surface/60 p-3">
              {['Outfit', 'Bondage', 'Toys', 'Dolls', 'Other'].map((cat) => {
                const catItems = realItemsCatalog.filter((it) => (it.category || 'Other') === cat);
                if (catItems.length === 0) return null;
                return (
                  <div key={cat}>
                    <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-game-accent/70">{cat}</p>
                    {catItems.map((it) => {
                      const checked = selectedRealItemIds.includes(String(it._id));
                      return (
                        <label key={it._id} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-xs text-game-body hover:bg-white/5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleRealItem(String(it._id))}
                            className="accent-game-accent"
                          />
                          {it.name}
                        </label>
                      );
                    })}
                  </div>
                );
              })}
              {realItemsCatalog.length === 0 ? <p className="text-xs text-game-muted">No items configured.</p> : null}
            </div>
          ) : null}
        </div>

        <div className="border-t border-white/10 px-6 py-4">
          <motion.button
            type="button"
            transition={snappyTween}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={onAccept}
            className="w-full rounded-xl bg-game-accent py-3 text-sm font-bold text-game-accentText"
          >
            I Accept
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
